import { useEffect, useRef, useState, type JSX } from "react";
import { Button, Modal } from "react-bootstrap";
import { z } from "zod";

import { SiteLogo } from "src/assets";

// .tsx, not the .jsx originally asked for — this project is TypeScript
// throughout (strict mode, no untyped files in src/), so matching that
// convention here rather than introducing the one .jsx file in the repo.
const VersionInfoSchema = z.object({
  version: z.string(),
  builtAt: z.string(),
});

const POLL_INTERVAL_MS = 3 * 60 * 1000;

/**
 * Detects when a newer build has been deployed while this tab is still open
 * on an older one. Polls /version.json (written at build time by
 * scripts/write-version.js — see that file) every minute and whenever the
 * tab becomes visible again, comparing against whichever version was live
 * when this component first mounted. On a mismatch, shows a dialog with a
 * "Reload" button rather than reloading automatically — unlike
 * ChunkErrorBoundary's chunk-load case, there's a real page the user might
 * be mid-task on here, so it asks first.
 */
export default function UpdateChecker(): JSX.Element | null {
  const [showBanner, setShowBanner] = useState(false);
  const baselineVersionRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const checkVersion = async () => {
      try {
        const response = await fetch("/version.json", { cache: "no-store" });
        if (!response.ok) return;

        // /version.json is our own build artifact, not a third-party API,
        // but it's still a network response — validate it like any other
        // fetched boundary rather than trusting the shape. A stale
        // cached/edge response (despite no-store) or a dev environment
        // with no version.json at all could return something unexpected.
        const parsed = VersionInfoSchema.safeParse(await response.json());
        if (!parsed.success || cancelled) return;

        if (baselineVersionRef.current === null) {
          // First check establishes the baseline — never show the banner
          // for the version the page already loaded with.
          baselineVersionRef.current = parsed.data.version;
          return;
        }

        if (parsed.data.version !== baselineVersionRef.current) {
          setShowBanner(true);
        }
      } catch {
        // Network blip / offline — skip this check silently. It just gets
        // retried on the next poll or visibility change; it should never
        // crash the app or falsely show the banner.
      }
    };

    void checkVersion();

    const intervalId = window.setInterval(
      () => void checkVersion(),
      POLL_INTERVAL_MS,
    );

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void checkVersion();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  if (!showBanner) return null;

  return (
    <Modal show centered backdrop="static" keyboard={false}>
      <Modal.Body className="text-center py-5 px-4">
        <img
          src={SiteLogo}
          alt="Foresight"
          style={{ height: 28 }}
          className="mb-4"
        />
        <h5 className="fw-bold mb-2">A new version of Foresight is here</h5>
        <p className="text-muted mb-4">
          We’ve released a new version with enhancements to improve your
          experience. Please click Reload to apply the updates.
        </p>
        <Button
          variant="purple"
          className="px-4"
          onClick={() => window.location.reload()}
        >
          Reload
        </Button>
      </Modal.Body>
    </Modal>
  );
}
